import { OctagonAlert, OctagonMinus } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { getExpireWarnings } from '@/actions';

const WarningBanner = ({ terminalId, terminalName }) => {
  const [warning, setWarning] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchWarning = async () => {
      if (!terminalId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getExpireWarnings();
        if (response?.data?.warnings) {
          const terminalWarning =
            response.data.warnings[terminalId] ||
            response.data.warnings[String(terminalId)];
          if (terminalWarning && terminalWarning.warning_type !== 'NORMAL') {
            setWarning(terminalWarning);
          }
        }
      } catch (err) {
        console.error('Error fetching expire warnings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarning();
  }, [terminalId]);

  const warningConfig = {
    INITIAL: {
      bgColor: 'bg-warning-initial',
      color: 'text-primary-text',
      icon: OctagonAlert,
    },
    FINAL: {
      bgColor: 'bg-warning-final',
      color: 'text-primary-text',
      icon: OctagonAlert,
    },
    SUSPENDED: {
      bgColor: 'bg-warning-suspend',
      color: 'text-white',
      icon: OctagonMinus,
    },
  };

  if (isLoading || !warning) {
    return null;
  }

  const config = warningConfig[warning.warning_type] || warningConfig.initial;

  const displayTerminalName =
    terminalName ||
    warning.terminal?.slug ||
    warning.terminal?.name ||
    'Terminal';

  const getMessage = () => {
    switch (warning.warning_type) {
      case 'INITIAL':
        return `${displayTerminalName} Terminal has missed appointments above the allowed threshold. Please confirm this appointment will be used or cancel it in advance.`;
      case 'FINAL':
        return `${displayTerminalName} Terminal is at risk of suspension due to missed appointments. Only book if you are sure this appointment will be used. If final warning continues for 7 consecutive days, booking access will be suspended.`;
      case 'SUSPENDED':
        return `Booking suspended, Access to ${displayTerminalName} Terminals is currently suspended due to missed appointments. You can still view existing appointments for this terminal.`;
      default:
        return `${displayTerminalName} Terminal has missed appointments above the allowed threshold. Please confirm this appointment will be used or cancel it in advance.`;
    }
  };

  const message = getMessage();

  return (
    <div className={`${config.bgColor} rounded p-2 md:p-3`}>
      <div className="flex items-center justify-between mb-2 md:mb-3">
        <div className="flex items-center space-x-2">
          <config.icon
            size={16}
            color={warning.warning_type === 'SUSPENDED' ? '#ffffff' : '#131517'}
          />
          <span className={`${config.color} font-bold text-xs md:text-sm`}>
            {displayTerminalName}:
          </span>
        </div>
        <div
          className={`bg-white font-averta text-primary-text px-3 h-6 rounded-full flex items-center justify-center font-bold text-sm`}>
          {warning.warning_type_text}
        </div>
      </div>

      <p
        className={` font-normal text-xs md:text-sm leading-relaxed m-0 ${config.color}`}>
        {message}
      </p>
    </div>
  );
};

export default WarningBanner;
